import { Fragment, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import {
  Avatar,
  Button,
  CommentIcon,
  DiamondLikeFilledIcon,
  DiamondLikeIcon,
  Icon,
  ShareIcon,
} from '@/components';
import { VideoReelModel } from '@/models';
import { useCreateLike, useDeleteLike, useTrackReel } from '../api';
import { ActionType } from '../const';
import { getAuth } from '@/libs/cookies';
import toast from 'react-hot-toast';
import { TFunction } from 'next-i18next';

interface VideoProps {
  video: VideoReelModel;
  t: TFunction;
  dispatch: (value: any) => void;
  onToggleComment: () => void;
  onPlayVideo: () => void;
}

const Video = ({
  video,
  t,
  dispatch,
  onToggleComment,
  onPlayVideo,
}: VideoProps) => {
  const { isAuth } = getAuth();

  const playerRef = useRef<ReactPlayer>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const [isClamped, setIsClamped] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const player = playerRef.current?.getInternalPlayer();
          if (entry.isIntersecting) {
            if (typeof player?.playVideo === 'function') player.playVideo();
            onPlayVideo();
          } else {
            if (typeof player?.pauseVideo === 'function') player.pauseVideo();
          }
        });
      },
      { threshold: 0.5 }
    );

    const videoContainerElement = videoContainerRef.current;

    if (videoContainerElement) {
      observer.observe(videoContainerElement);
    }

    return () => {
      if (videoContainerElement) {
        observer.unobserve(videoContainerElement);
      }
    };
  }, [onPlayVideo]);

  useEffect(() => {
    setTimeout(() => {
      const element = contentRef.current;
      if (element && element.scrollHeight > element.clientHeight) {
        setIsClamped(true);
      }
    }, 100);
  }, []);

  const { mutate: dislike, isLoading: loadingDislike } = useDeleteLike();

  const { mutate: like, isLoading: loadingLike } = useCreateLike();

  const { refetch: trackReel, isLoading: isTracking } = useTrackReel(video.id);

  const handleLike = (video: VideoReelModel) => {
    if (!isAuth) {
      return toast.error(t('message.unauthorized'));
    }

    let likeCount = video.like_count;
    if (video.is_liked) {
      dislike(video.id);
      likeCount--;
    } else {
      like({ reelId: video.id, body: null });
      likeCount++;
    }
    dispatch({
      type: ActionType.ToggleLike,
      payload: {
        ...video,
        is_liked: !video.is_liked,
        like_count: likeCount,
      },
    });
  };

  return (
    <Fragment>
      <div className="tw-relative tw-h-full">
        <div
          ref={videoContainerRef}
          className="tw-relative tw-h-full tw-bg-black tw-rounded-none sm:tw-rounded-lg tw-overflow-hidden tw-border-none sm:tw-border"
        >
          <ReactPlayer
            url={video.youtube_video_url}
            width="100%"
            height="100%"
            className="react-player"
            style={{ textAlign: 'center', backgroundColor: 'black' }}
            ref={playerRef}
            playing={false}
            controls={false}
            loop={true}
            onStart={() => {
              trackReel();
            }}
          />

          {/* Description */}
          <div className="tw-absolute tw-left-4 tw-bottom-4">
            <div className="tw-flex tw-gap-x-2 tw-items-center tw-text-white tw-text-sm tw-mb-3">
              <Avatar
                src={video.staffs.avatar}
                className="tw-w-7 tw-h-7"
                rounded="tw-rounded-full"
              />
              <h3
                className="tw-font-semibold"
                dangerouslySetInnerHTML={{ __html: video.staffs.fullname }}
              ></h3>
            </div>
            <div className="tw-text-white tw-text-sm tw-w-[280px]">
              <div
                ref={contentRef}
                className={
                  expanded ? 'tw-font-light' : 'text-2line tw-font-light'
                }
              >
                {video.description}
              </div>
              {isClamped && (
                <span
                  onClick={() => setExpanded(!expanded)}
                  className="tw-font-semibold tw-cursor-pointer"
                >
                  {expanded ? 'Ẩn bớt' : 'Xem thêm'}
                </span>
              )}
            </div>
          </div>
        </div>
        {/* Actions */}
        <div className="tw-absolute tw-right-3 tw-bottom-3 sm:-tw-right-[60px] sm:tw-bottom-0">
          <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-flex-col tw-gap-1 tw-items-center">
              <Button
                disabled={false}
                loading={false}
                onClick={() => handleLike(video)}
                className="tw-flex tw-justify-center tw-p-2 !tw-rounded-full !tw-w-[40px] !tw-h-[40px] tw-bg-white/[.5] sm:tw-bg-primary-main/[.08] tw-border-none"
              >
                <Icon
                  className="tw-text-white sm:tw-text-primary-main"
                  icon={
                    video.is_liked ? (
                      <DiamondLikeFilledIcon />
                    ) : (
                      <DiamondLikeIcon />
                    )
                  }
                />
              </Button>
              <p className="tw-text-sm tw-font-light tw-text-white sm:tw-text-secondary-70">
                {video.like_count}
              </p>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-1 tw-items-center">
              <Button
                disabled={false}
                loading={false}
                onClick={() => {
                  onToggleComment();
                }}
                className="tw-flex tw-justify-center tw-p-2 !tw-rounded-full !tw-w-[40px] !tw-h-[40px] tw-bg-white/[.5] sm:tw-bg-primary-main/[.08] tw-border-none"
              >
                <Icon
                  className="tw-text-white sm:tw-text-primary-main"
                  icon={<CommentIcon />}
                />
              </Button>
              <p className="tw-text-sm tw-font-light tw-text-white sm:tw-text-secondary-70">
                {video.total_comment_count}
              </p>
            </div>
            <div className="tw-flex-col tw-gap-1 tw-items-center tw-hidden">
              <Button
                disabled={false}
                loading={false}
                onClick={() => {}}
                className="tw-flex tw-justify-center tw-p-2 !tw-rounded-full !tw-w-[40px] !tw-h-[40px] tw-bg-white/[.5] sm:tw-bg-primary-main/[.08] tw-border-none"
              >
                <Icon
                  className="tw-text-white sm:tw-text-primary-main"
                  icon={<ShareIcon />}
                />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Video;
